import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject, DOCUMENT } from '@angular/core';

import { ActivatedRoute } from '@angular/router';
import moment from 'moment';

import { EmployeeInfoService } from '@core/services/employee-info.service';
import { PortalSettingsService } from '@core/services/portal-settings.service';
import { UtilityService } from '@core/services/utility.service';
import { ChecklistService } from '@pages/checklist/services/checklist.service';
import { DailyReportsService } from '@pages/field/service/daily-reports.service';
import { EmployeeService } from '@pages/employee/services/employee.service';
import { SmService } from '@pages/sm/services/sm.service';
import { HomeService } from '@pages/home/services/home.service';
import { ClockInOutService } from '@core/services/clock-in-out.service';
import { LineType } from '@shared/model/line-type.model';
import { SimpleLookupResult } from '@typeDefs/general/general';
import { MechanicFormData } from '@shared/model/mechanic-type-data.model';
import { JobFormData } from '@shared/model/job-type-data.model';
import { ClockAction_Request } from '@typeDefs/employee/requests/employee';
import { PostClockAction_Result } from '@typeDefs/employee/responses/employee';

@Component({
    selector: 'app-clock-in-out',
    templateUrl: './clock-in-out.component.html',
    styleUrls: ['./clock-in-out.component.css'],
    standalone: false
})
export class ClockInOutComponent implements OnInit, OnDestroy {
  @ViewChild('signaturePad', { static: false }) signaturePad: any;
  @ViewChild('clockInOutPage', { static: true }) clockInOutPage: ElementRef;

  private readonly document = inject(DOCUMENT);
  private readonly portalSettingsService = inject(PortalSettingsService);
  private readonly employeeInfoService = inject(EmployeeInfoService);
  private readonly employeeService = inject(EmployeeService);
  private readonly dailyReportsService = inject(DailyReportsService);
  private readonly checklistService = inject(ChecklistService);
  private readonly route = inject(ActivatedRoute);
  private readonly utilityService = inject(UtilityService);
  private readonly smService = inject(SmService);
  private readonly homeService = inject(HomeService);
  private readonly clockinoutService = inject(ClockInOutService);

  private readonly window = this.document.defaultView!;
  private redirectTimeout: any;
  private panelWidth: number;

  portalSettings = this.portalSettingsService.getPortalSettings();
  employeeInfo = this.employeeInfoService.getEmployeeInfo();
  geolocationEnabled = false;
  requestingLocation = false;
  position: any = null;
  clockActionPerformed = false;
  performingClockAction = false;
  customMsg = this.portalSettings.CustomVerbiageClockOutSign.ConfigValue;
  formData: any = {};
  loadingClockStatus = false;
  clockStatus: any;
  alert: { type: string; msg: string } = null;
  clockMoment: moment.Moment;
  lastClockDisplay: string;
  requireSign = this.portalSettings.RequireSignClockOut.ConfigValue == '1';
  signatureData: any;
  clockIn: any;
  clockActionSuccess: any;
  message: string;
  rawImgData: any;
  signatureNEmpty: any;
  submitSignImg: any = null;
  hideClick: boolean;
  showClickMsg: boolean;
  signed = false;
  read = false;
  redBorder = false;
  disableSubmitSign: boolean;
  kioskLogin = false;
  params: any;
  types: LineType[] = [];
  isJobEnabled = false;
  isServiceEnabled = false;
  isMechanicTypeEnabled = false;
  isUserFSTech: boolean;
  workOrderList: any = [];
  scopeList: any = [];
  payTypeList: any = [];
  lastClockinType: any;
  licensedUser: any = {};
  signaturePadOptions: Object = {
    canvasWidth: 550,
    canvasHeight: 150,
  };

  mealBreak = false;
  mealBreakSetting: { [key: string]: string } = {};
  mealBreakOptions: SimpleLookupResult[] = [
    { ComboType: null, DatabaseValue: 'N', DisplayValue: 'No' },
    { ComboType: null, DatabaseValue: 'Y', DisplayValue: 'Yes' },
  ];
  hadMealBreakToday = false;
  hasWarning = false;

  async ngOnInit(): Promise<void> {
    this.params = this.route.snapshot.queryParams;
    this.kioskLogin = this.params?.kioskLogin;
    this.isUserFSTech = this.employeeInfo.IsActiveTechnician;
    this.clockinoutService.resetJobFormData(this.formData);
    this.formData.selectedType = null;
    this.formData.selectedWorkOrder = null;
    this.formData.selectedScope = null;
    this.formData.selectedPayType = null;
    this.formData.mealBreak = 'N';
    this.clockinoutService.resetMechanicFormData(this.formData);
    const width = this.clockInOutPage?.nativeElement?.clientWidth;
    this.panelWidth = (width > 550 ? 550 : width) + 30;
    this.licensedUser = await this.homeService.getLicenseCounts(this.employeeInfo.PRCo, this.employeeInfo.Employee);
    this.getMealBreakSetting();
    this.createTypeDropDownValues();
    this.getLocation();
    this.getClockInStatus();
  }

  ngOnDestroy(): void {
    clearTimeout(this.redirectTimeout);
  }

  getMealBreakSetting(): void {
    if (this.portalSettings.ClockInMealBreakMinutes.ConfigValue != null) {
      this.portalSettings.ClockInMealBreakMinutes.ConfigValue.split(';').forEach((val: string) => {
        if (val) {
          const settingParts = val.split(',');
          this.mealBreakSetting[settingParts[0] + ',' + settingParts[1]] = settingParts[2];
        }
      });
    }
  }

  showMealBreak(): boolean {
    return this.mealBreakSetting[this.employeeInfo.PRCo + ',' + this.employeeInfo.PRGroup] != null && !this.hadMealBreakToday;
  }

  postClockAction(ignoreLocation: boolean): void {
    if (!this.loadingClockStatus && !this.requestingLocation) {
      this.performingClockAction = true;
      const requestObj: ClockAction_Request = {
        GPSLat: null,
        GPSLong: null,
        JCJMKeyID: null,
        JCJPKeyID: null,
        SignatureData: null,
        ClockTimeString: null,
        SMTripID: null,
        SMWorkOrderScopeID: null,
        SMPayType: null,
        MealBreak: this.formData.mealBreak == 'Y',
        EMWorkOrderKeyID: null,
        EMWorkOrderItemKeyID: null,
        EquipmentKeyID: null,
        EMCostCodeKeyID: null,
        FromGroupClock: false,
        KioskPass: null,
      };
      if (!ignoreLocation && this.position) {
        requestObj.GPSLat = this.position.coords.latitude;
        requestObj.GPSLong = this.position.coords.longitude;
      }
      if (this.formData.selectedType == 'J' && !this.clockStatus) {
        requestObj.JCJMKeyID = this.formData.selectedJob.JCJMKeyID;
        requestObj.JCJPKeyID = this.formData.selectedPhase.JCJPKeyID;
        if (this.formData.selectedEquipment) requestObj.EquipmentKeyID = this.formData.selectedEquipment.KeyID;
      } else if (this.formData.selectedType == 'S' && !this.clockStatus) {
        requestObj.SMTripID = this.formData.selectedWorkOrder.SMTripID;
        requestObj.SMWorkOrderScopeID = this.formData.selectedScope.SMWorkOrderScopeID;
        requestObj.SMPayType = this.formData.selectedPayType.DatabaseValue;
      } else if (this.formData.selectedType == 'M' && !this.clockStatus) {
        requestObj.EMWorkOrderKeyID = this.formData.selectedEMWorkOrder?.KeyID;
        requestObj.EMWorkOrderItemKeyID = this.formData.selectedEMWorkOrderItem?.KeyID;
        requestObj.EquipmentKeyID = this.formData.selectedEquipment?.KeyID;
        requestObj.EMCostCodeKeyID = this.formData.selectedEMCostCode?.KeyID;
      }
      if (this.signatureData != null) requestObj.SignatureData = this.signatureData;

      const currentTime = moment();
      requestObj.ClockTimeString = currentTime.format('MM/DD/YYYY h:mm:ss a');

      this.employeeService.postClockAction(requestObj).then((response: PostClockAction_Result) => {
        if (response != null) {
          this.clockActionPerformed = true;
          this.clockIn = response.resultClockAction;
          this.clockActionSuccess = response.success;
          this.hasWarning = response.warningMessage != null && response.warningMessage != '';
          if (response.success) {
            if (response.resultClockAction) {
              this.message = 'Successfully clocked in at ' + currentTime.format('hh:mm A');
              this.redirectToLogin();
            } else if (response.resultClockAction == false) {
              if (response.warningMessage) {
                this.alert = { type: 'warning', msg: 'Successfully clocked out at ' + currentTime.format('hh:mm A') + ', ' + response.warningMessage };
              } else {
                this.message = 'Successfully clocked out at ' + currentTime.format('hh:mm A');
                this.redirectToLogin();
              }
            }
          } else {
            this.message = response.error;
          }
        }
      });
    }
  }

  typeChanged(): void {
    if (this.formData.selectedType == 'J') {
      this.resetJobDropdown();
    } else if (this.formData.selectedType == 'S') {
      this.resetServiceDropdown();
      this.getWorkOrder();
    } else if (this.formData.selectedType == 'M') {
      this.clockinoutService.resetMechanicFormData(this.formData);
    }
  }

  resetJobDropdown(): void {
    this.clockinoutService.resetJobFormData(this.formData);
  }

  resetServiceDropdown(): void {
    this.formData.selectedWorkOrder = null;
    this.formData.selectedScope = null;
    this.formData.selectedPayType = null;
  }

  createTypeDropDownValues(): void {
    this.types = this.clockinoutService.getEnabledLineTypes(this.licensedUser, this.isUserFSTech);
    this.isJobEnabled = this.types?.some(type => type.Value == 'J');
    this.isServiceEnabled = this.types?.some(type => type.Value == 'S');
    this.isMechanicTypeEnabled = this.types?.some(type => type.Value == 'M');
  }

  loadJobOrServiceDropDown(): void {
    this.formData.selectedType =
      this.types?.length == 1 || !this.types?.some(lineType => lineType.Value == this.lastClockinType)
        ? this.types[0].Value
        : this.lastClockinType;
    if (this.formData.selectedType == 'S') this.getWorkOrder();
  }

  async getWorkOrder(): Promise<void> {
    const response = await this.smService.getWorkOrderTripsByEmployee(this.employeeInfo.Employee, this.employeeInfo.PRCo);
    if (response != null && Array.isArray(response)) {
      this.workOrderList = [];
      this.workOrderList = response.map((val: any) => {
        const scheduledDateDisplay = val.ScheduledDate != null ? moment(val.ScheduledDate).utc().format('DD/MMM') : '';
        return {
          WorkOrder: val.WorkOrder,
          SMTripID: val.SMTripID,
          SMWorkOrderID: val.SMWorkOrderID,
          ScheduledDateDisplay: scheduledDateDisplay,
          Display: (scheduledDateDisplay != '' ? scheduledDateDisplay + ' - ' : '') + 'WO #' + val.WorkOrder + ' ' + val.TripDescription,
          SMCo: val.SMCo,
        };
      });
    }
  }

  workOrderChanged(): void {
    if (this.formData.selectedWorkOrder != null) {
      this.formData.selectedScope = null;
      this.formData.selectedPayType = null;
      this.getScope();
      this.getPayType();
    }
  }

  async getScope(): Promise<void> {
    this.scopeList = [];
    const response = await this.smService.getWorkOrderScopes('?sMWorkOrderID=' + this.formData.selectedWorkOrder.SMWorkOrderID);
    if (response != null && Array.isArray(response)) {
      this.scopeList = response;
      this.scopeList = response.map(val => ({
        ...val,
        Display: val.Scope + ' - ' + val.ScopeDetails,
      }));
    }
  }

  async getPayType(): Promise<void> {
    this.payTypeList = [];
    const smCo = this.formData.selectedWorkOrder?.SMCo;
    if (smCo != null) {
      const response = await this.smService.getSMPayTypes(smCo);
      if (response != null && Array.isArray(response)) {
        this.payTypeList = response.map(val => ({
          ...val,
          Display: val.DatabaseValue + ' - ' + val.DisplayValue,
        }));
      }
    }
  }

  disableClockInButton(): boolean {
    return (
      this.loadingClockStatus ||
      this.performingClockAction ||
      //disable if Type is J but form is not valid
      (this.formData.selectedType == 'J' && !this.formData.isJobFormValid) ||
      //disable if Type is S but work order/scope/pay type combined is not selected
      (this.formData.selectedType == 'S' &&
        (this.formData.selectedWorkOrder == null || this.formData.selectedScope == null || this.formData.selectedPayType == null)) ||
      //disable if Type is M but mechanic form combined is not selected
      (this.formData.selectedType == 'M' && !this.formData.isMechanicformValid)
    );
  }

  canMealBreakClockIn(): boolean {
    if (this.mealBreakSetting[this.employeeInfo.PRCo + ',' + this.employeeInfo.PRGroup] != null) {
      const mealBreakThreshold = parseInt(this.mealBreakSetting[this.employeeInfo.PRCo + ',' + this.employeeInfo.PRGroup]);
      //convert utc clock in time to local time
      const lastClockOut = moment(this.clockMoment.format('YYYY-MM-DD HH:mm:ss'));
      const minutesElapsed = moment().diff(lastClockOut, 'minutes');
      if (minutesElapsed < mealBreakThreshold) return false;
    }
    return true;
  }

  clockOutClicked(): void {
    if (this.requireSign) {
      this.checkSigned();
      if (this.formData.typeSign) {
        this.signatureData = this.rawImgData;
        this.postClockAction(false);
      } else if (this.signatureNEmpty) {
        this.signatureData = this.signaturePad.toDataURL().replace(/^data:image\/(png|jpg);base64,/, '');
        this.postClockAction(false);
      }
    }
  }

  clickSign(type: any): void {
    if (this.signaturePad.isEmpty()) {
      this.formData.typeSign = true;
      this.checklistService.getTypeSign(type).then((response: any) => {
        if (response != null) {
          this.rawImgData = response;
          this.submitSignImg = 'data:image/png;base64,' + response;
        }
      });
      this.hideClick = true;
      this.showClickMsg = false;
    } else {
      this.showClickMsg = true;
    }
  }

  clear(): void {
    if (this.signaturePad != null) this.signaturePad.clear();
    this.signatureNEmpty = false;
    this.rawImgData = null;
    this.submitSignImg = null;
    this.formData.typeSign = false;
    this.showClickMsg = false;
    this.hideClick = false;
  }

  searchJobs(term: string, item: any): boolean {
    term = term.trim().toLowerCase();
    return item.JCJMKeyID?.toString().indexOf(term) > -1 || item.DisplayValue?.toLowerCase().indexOf(term) > -1;
  }

  searchPhases(term: string, item: any): boolean {
    term = term.trim().toLowerCase();
    return (
      item.JCJPKeyID?.toString().indexOf(term) > -1 ||
      item.PhaseDescription?.toLowerCase().indexOf(term) > -1 ||
      item.PhaseGroup?.toString().indexOf(term) > -1
    );
  }

  private getClockInStatus(): void {
    this.loadingClockStatus = true;
    const currentTime = moment();
    const currentTimeString = currentTime.format('MM/DD/YYYY h:mm:ss a');
    const requestParam = 'fromFieldKiosk=' + false + '&currentTimeString=' + currentTimeString + '&where=' + 2;
    this.employeeService.getClockInStatus(requestParam).then((response: any) => {
      if (response != null) {
        this.clockStatus = response.ClockAction;
        this.lastClockinType = response.LastUsedClockinType == null ? 'J' : response.LastUsedClockinType;
        this.loadingClockStatus = false;
        this.clockMoment = moment.utc(response.ClockTime);
        this.lastClockDisplay = this.clockMoment.format('hh:mm A');
        this.mealBreak = response.MealBreak == 'Y';
        this.hadMealBreakToday = response.HadMealBreakToday;
        if (!this.clockStatus && (this.isJobEnabled || this.isServiceEnabled || this.isMechanicTypeEnabled)) {
          this.loadJobOrServiceDropDown();
        } else {
          //automatic posting of clock action
          if (
            !this.requestingLocation &&
            this.geolocationEnabled &&
            (!this.requireSign || !this.clockStatus) &&
            (!this.clockStatus || !this.showMealBreak()) &&
            (!this.mealBreak || this.canMealBreakClockIn())
          ) {
            this.postClockAction(false);
          }
        }
      }
    });
  }

  private redirectToLogin(): void {
    this.redirectTimeout = setTimeout(() => {
      this.performLoginRedirect();
    }, 5000);
  }

  performLoginRedirect(): void {
    let loginURL = '/Account/Login';
    this.utilityService.isLoggedOut = true;
    if (this.kioskLogin) {
      this.window.location.href = '/Account/Logout?kioskLogout=true';
      loginURL = '/Account/Kiosklogin';
    } else {
      this.window.location.href = '/Account/Logout';
    }
    this.window.history.replaceState(null, null, loginURL);
  }

  private getLocation(): void {
    this.requestingLocation = true;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position: any) => {
        setTimeout(() => {
          this.requestingLocation = false;
          this.geolocationEnabled = true;
          this.position = position;

          //Prevent auto clock action if user is using job/phase while clocking in, or if user is using signature while clocking out, or if still loading clock status
          if (
            ((!this.isJobEnabled && !this.isServiceEnabled && !this.isMechanicTypeEnabled) || this.clockStatus) &&
            !this.loadingClockStatus &&
            (!this.requireSign || !this.clockStatus) &&
            (!this.clockStatus || !this.showMealBreak()) &&
            (!this.mealBreak || this.canMealBreakClockIn())
          ) {
            this.postClockAction(false);
          }
        }, 1000);
      });
      this.requestingLocation = false;
    } else {
      this.requestingLocation = false;
    }
  }

  onJobDropdownChange(data: JobFormData): void {
    this.clockinoutService.updateJobFormData(this.formData, data);
  }

  private checkSigned(): void {
    if (this.signaturePad.isEmpty() == false) {
      this.signatureNEmpty = true;
      this.redBorder = false;
      this.disableSubmitSign = true;
    } else {
      this.redBorder = true;
    }
  }

  onMechanicDropdownChange(data: MechanicFormData): void {
    this.clockinoutService.updateMechanicFormData(this.formData, data);
  }

  closeAlert(): void {
    this.alert = null;
  }

  showClockInTypes(): boolean {
    return (
      (this.isJobEnabled || this.isServiceEnabled || this.isMechanicTypeEnabled) &&
      !this.clockStatus &&
      !this.clockActionPerformed &&
      !this.loadingClockStatus
    );
  }

  showClockinfromMealBreak(): boolean {
    return (
      this.mealBreak &&
      !this.canMealBreakClockIn() &&
      !this.clockStatus &&
      !this.isJobEnabled &&
      !this.isServiceEnabled &&
      !this.isMechanicTypeEnabled &&
      !this.clockActionPerformed
    );
  }

  showProcessing(): boolean {
    return !this.isJobEnabled && !this.isServiceEnabled && !this.isMechanicTypeEnabled && this.clockStatus;
  }

  showRegularClockInButtons(): boolean {
    return (
      (!this.mealBreak || this.canMealBreakClockIn()) &&
      ((!this.isJobEnabled && !this.isServiceEnabled && !this.isMechanicTypeEnabled) || this.clockStatus) &&
      !this.clockActionPerformed &&
      (!this.requireSign || (this.requireSign && !this.clockStatus))
    );
  }
}
